/* eslint-disable dot-notation */
// ============================================================
// 離線打卡 Local Queue
// ------------------------------------------------------------
// 基於 IndexedDB 的 store，用來存放「因可重試錯誤（5xx / timeout / network error）而送不出去」的打卡紀錄。
// Scheduler 之後會用 backoff 的方式重送這些紀錄。
//
// Scope 界線（重要）：
//   - 三種紀錄共用本 store：
//       同步 POST /punch-in-records/ 的失敗重送紀錄
//       非同步 /enqueue 紀錄（帶 idempotencyKey / asyncStatus / docKey，兼作 Firestore 監聽的追蹤把手）
//       打卡機紀錄（punchKind: 'machine'，新紀錄走卡機 /enqueue，舊紀錄走同步補送）
//   - 「留在 queue 重送」的情境只有：有網路但 server 不穩（5xx / timeout / network error）且 canEnqueue
//   - 真離線（navigator.onLine === false）不留 queue：write-first 會先寫入，但失敗當下即被移除
//
// 流程（write-first，同步/非同步皆無條件先寫）：
//   點擊 → enqueue {status: 'syncing'} → 呼叫 API
//     成功                  → remove（非同步為 Firestore succeeded + getPunchInCall 後 remove）
//     4xx / 不可留 queue    → remove
//     5xx / ...（canEnqueue）→ update {status: 'pending', punchedAt, retryCount, nextRetryAt, lastError}
//
// 建立超過 7 天的紀錄會由 purgeExpired 自動清掉（不管目前是什麼 status）。
//
// 多帳號隔離：
//   - 每筆紀錄都帶 `accountId`，切換使用者時不會混到別人的 queue，Scheduler 在讀取時根據當前 userId 篩選
//   - 登出時不做整批清除
// ============================================================

import { openDB, IDBPDatabase, DBSchema } from 'idb';
import { v4 as uuidv4 } from 'uuid';
import { createZonedMoment } from '@src/utils/userTimeZone';
import { isProd } from '@src/utils/env';

import { IPunchInRecord, TimeClockPunchPayload, TimeClockPunchResult } from '@src/services/punchIn';
import { PunchTaskStatus } from '@src/utils/punch/punchTaskStatus';
import { getRecognizeDateFromTime } from '@src/utils/rollover';

// 一筆紀錄的生命週期狀態（= local queue 的「擁有權」，實際進度另看 asyncStatus）：
//   syncing  → in-flight，不歸 scheduler 管：HTTP 呼叫進行中，或（非同步）已 202 交棒 BE 管線 + Firestore watcher。
//              如果送 API 到一半 browser 被關掉：
// 							非同步紀錄（有 idempotencyKey、尚未交棒）由 reclaimStaleSyncing 在下次 scan 拉回 pending 重送（visibility timeout）；
// 							同步紀錄無冪等保護不回收，留到 TTL 清掉
//   pending  → API 失敗後落入這個狀態，等待 scheduler 下次 tick 重送
//   inactive → 重試次數達 MAX_RETRY_COUNT 後的終點狀態，不會再被自動重送，等人工上傳或 TTL 清除
export type LocalQueueStatus = 'syncing' | 'pending' | 'inactive';

// V2 代表前景非同步 request 的 reclaim 起算點已經透過 IDB write durable 化。
// 舊 bundle 寫下的紀錄沒有版本：pending / inactive / Firestore outstanding 照舊處理，
// 但 syncing 不做自動 reclaim，維持到 TTL，避免用不可靠的 createdAt 提前補送。
export const CURRENT_QUEUE_RECORD_VERSION = 2 as const;

// 打卡時間改成 server 收到時間時，需要用「送出當下所選 shift」的 rollover 規則重算歸屬日。
// 快照不可事後查 current shift：設定可能已更新，cached punch-in-info 也可能是當時唯一資料來源。
export interface RolloverSnapshot {
	// 舊紀錄缺值時以 Asia/Taipei 解讀；不依補送當下的裝置時區重算。
	timezone?: string;
	isRolloverPreviousDay: boolean;
	dailyRolloverHour: number;
	dailyRolloverMinute: number;
}

// publish_failed republish 在送 HTTP 前先持久化 lease。nextIdempotencyKey 跟著 lease 保存，
// lease 過期由另一個 tab 接手時仍重放同一把 key，不會建立第二個 BE task。
export interface RepublishLease {
	sourceIdempotencyKey: string;
	sourceDocKey: string;
	sourceRetryCount: number;
	ownerId: string;
	nextIdempotencyKey: string;
	expiresAt: number;
}

// 新 bundle 對 async local retry 的 in-flight ownership。claimId 是 callback 的 fencing token；
// expiresAt 只負責 crash recovery，接手後仍重送原本的 idempotencyKey。
export interface AsyncRetryClaim {
	claimId: string;
	expiresAt: number;
}

interface BaseQueueRecord {
	// UUID v4，單筆紀錄的唯一識別（IDB keyPath，不可變），注意：BE 去重鍵走另一欄 idempotencyKey（可輪替），不是這個 id
	id: string;
	// 寫入當下登入的使用者 ID（= user.profile.id）。
	// Retry 時用來過濾「只送當前使用者的 queue」，避免 A 登出 B 登入時把 A 的打卡送出去
	accountId: string;
	status: LocalQueueStatus;
	// 缺值代表舊格式；目前只有 V2 foreground request 能被 stale-syncing reclaim。
	queueRecordVersion?: typeof CURRENT_QUEUE_RECORD_VERSION;
	// 已重試幾次。0 代表還沒重試過，跟 RETRY_BACKOFF_MS 的 index 對應
	retryCount: number;
	// Date.now() ms；status=syncing 時為 0，代表還不能被 scheduler 撿走
	nextRetryAt: number;
	// 使用者點下打卡按鈕的當下
	createdAt: number;
	// HTTP request 實際送出的時刻（= enqueue 寫入 IDB 完成、caller 即將 dispatch 的當下）。
	// stale-syncing 回收門檻要用它而不是 createdAt，理由是**生命週期凍結**（不是 IDB 慢）：手機鎖屏 / 分頁被 freeze 時，await enqueue() 的 continuation 不會被執行，恢復後才 dispatch。
	// 例：00s 按下（壓 createdAt）→ 凍結 20s → 20s 才送出 → 該請求自己的 timeout 落在 50s，但用 createdAt 量的回收門檻在 39s 就開火，中間 11s 會回收一筆還活著的正常請求（補送帶 punch_datetime，先到 BE 就讓正常打卡被記成離線補登的本地時間）。
	// V2 會在 dispatch 前以 guarded IDB transaction 寫入；缺值的舊紀錄不參與 stale reclaim。
	requestStartedAt?: number;
	// CTA 樂觀顯示與 retry 補送用的「打卡時間」，retry 時塞進 payload 的 punch_datetime：
	//   非同步前景 enqueue → 寫入當下即壓（= createdAt，等待期間按鈕就顯示時間）；202 回 document_created_at 時覆蓋成 server 收到時間
	//   同步紀錄 → 轉 pending 時才壓（= createdAt），in-flight 的初始寫入不顯示
	punchedAt?: number;
	// 打卡歸屬日 (YYYY-MM-DD)。enqueue 當下用 punchedAt + 對應 shift 的 rollover 設定 (is_rollover_previous_day / daily_rollover_hour / daily_rollover_minute) 推算
	recognizeDate?: string;
	// enqueue 當下的 rollover 規則；punchedAt 改變時用它在同一 transaction 重算 recognizeDate。
	rolloverSnapshot?: RolloverSnapshot;
	// 最近一次失敗的 error 訊息，debug 用
	lastError?: string;
	// 當初 syncing→pending 那次 API 失敗的歸類字串（如 'http_502' / 'timeout' / 'network_error' / 'no_response'）。
	// 只在第一次轉 pending 時寫入並從此不變，後續 retry 失敗只更新 lastError，不動這個欄位。
	// retry 時會塞進 payload.deferred_reason 給 BE 做初步歸因。上限 64 chars。
	deferredReason?: string;
	// enqueue 當下選擇的打卡地點顯示名稱（IP setting / 位置 / QR code 的 name）。離線時打卡紀錄 log 頁要顯示，BE 無法事後查到，所以在 enqueue 當下快照
	displayLocationName?: string;
	// enqueue 當下對應 shift 的 shift_name。同上，離線 log 頁顯示用
	displayShiftName?: string;
	// location 4xx / Firestore fail 跨頁後重打時沿用原設定能力；不可從 remount 後的當前選項猜。
	allowPunchInOutOfRange?: boolean;
	// 送 BE 的去重鍵，走 HTTP header X-Idempotency-Key（不放 body）；也是 docKey 的後半段。
	idempotencyKey?: string;
	// 要監聽的 Firestore document key，格式 `{user_id}_{idempotencyKey}`。
	docKey?: string;
	// 這筆分同步打卡的當下狀態。「有值」本身即代表「已交棒給 BE、由 Firestore 監聽收尾」→ offlinePunchScheduler 不再對它做 local retry。
	asyncStatus?: PunchTaskStatus;
	// 一旦 enqueue 成功就不能刪除本機追蹤資料；republish 清掉 docKey 時仍保留此標記。
	serverAcceptedAt?: number;
	// 前景（true / 未設）vs 背景補送（false）的判別欄：listener 據此分文案家族與失敗開 modal 或 toast；
	// watcher republish 據此決定帶不帶 punch_datetime（前景不帶，避免被 BE 標成離線補登）。
	allowResultModal?: boolean;
	// publish_failed /enqueue 的跨 tab ownership；只由下方 guarded republish APIs 修改。
	republishLease?: RepublishLease;
	// pending async record 被 scheduler / 手動上傳 claim 後的 persisted lease。
	// 缺值的舊 syncing record 不自動回收，維持相容到 TTL。
	asyncRetryClaim?: AsyncRetryClaim;
}

// /hr 首頁一般打卡（同步 POST / 非同步 /enqueue 共用）
export interface RegularQueueRecord extends BaseQueueRecord {
	punchKind?: 'regular';
	payload: IPunchInRecord;
}

// 打卡機打卡（/hr/punch-in/machine）；有 idempotencyKey 走 server queue，舊紀錄走同步補送。
export interface MachineQueueRecord extends BaseQueueRecord {
	punchKind: 'machine';
	payload: TimeClockPunchPayload;
	machineResult?: TimeClockPunchResult;
}

export type LocalQueueRecord = RegularQueueRecord | MachineQueueRecord;

export type MachineQueueEdit = Pick<TimeClockPunchPayload, 'duty' | 'shift_id' | 'record_type'>;
export type MachineQueueOperationResult = 'claimed' | 'deleted' | 'busy' | 'locked' | 'not_found';

export function isMachineQueueLocked(record: MachineQueueRecord): boolean {
	return record.serverAcceptedAt != null || !!record.docKey || record.asyncStatus != null;
}

export type LocalQueueDeleteResult = 'deleted' | 'busy' | 'locked' | 'not_found';

export function isLocalQueueDeleteLocked(record: LocalQueueRecord): boolean {
	return record.serverAcceptedAt != null || !!record.docKey || record.asyncStatus != null || !!record.republishLease;
}

export function decideLocalQueueDeletion(
	record: LocalQueueRecord | undefined,
	accountId: string,
): LocalQueueDeleteResult {
	if (!record || record.accountId !== accountId) return 'not_found';
	if (isLocalQueueDeleteLocked(record)) return 'locked';
	if (record.status === 'syncing') return 'busy';
	return 'deleted';
}

// 狀態檢查與刪除共用 transaction，與另一分頁的 retry claim 互斥。
export async function deleteLocalQueue(id: string, accountId: string): Promise<LocalQueueDeleteResult> {
	const db = await getDb();
	const tx = db.transaction(STORE_NAME, 'readwrite');
	observeTransactionRejection(tx);
	const store = tx.objectStore(STORE_NAME);
	const result = decideLocalQueueDeletion(await store.get(id), accountId);
	if (result === 'deleted') await store.delete(id);
	await tx.done;
	if (result === 'deleted') notify();
	return result;
}

// The same transaction checks current ownership and saves the edited payload before claiming it.
// An automatic request already in flight must finish before either edit or delete can proceed.
export function decideMachineQueueOperation(
	record: LocalQueueRecord | undefined,
	accountId: string,
	edit: MachineQueueEdit | null,
	claimId: string,
	now: number,
): { result: MachineQueueOperationResult; record?: MachineQueueRecord } {
	if (!record || record.accountId !== accountId || record.punchKind !== 'machine' || !record.idempotencyKey) {
		return { result: 'not_found' };
	}
	if (isMachineQueueLocked(record)) return { result: 'locked' };
	if (record.status === 'syncing') return { result: 'busy' };
	if (!edit) return { result: 'deleted' };
	return {
		result: 'claimed',
		record: {
			...record,
			payload: { ...record.payload, ...edit },
			status: 'syncing',
			nextRetryAt: now,
			asyncRetryClaim: { claimId, expiresAt: now + ASYNC_RETRY_CLAIM_LEASE_MS },
		},
	};
}

export async function mutateMachineQueue(
	id: string,
	accountId: string,
	edit: MachineQueueEdit | null,
): Promise<ReturnType<typeof decideMachineQueueOperation>> {
	const db = await getDb();
	const tx = db.transaction(STORE_NAME, 'readwrite');
	observeTransactionRejection(tx);
	const store = tx.objectStore(STORE_NAME);
	const decision = decideMachineQueueOperation(await store.get(id), accountId, edit, uuidv4(), Date.now());
	if (decision.record) await store.put(decision.record);
	else if (decision.result === 'deleted') await store.delete(id);
	await tx.done;
	if (decision.result === 'claimed' || decision.result === 'deleted') notify();
	return decision;
}

export async function saveMachineResult(
	id: string,
	source: AsyncDocumentSource,
	result: TimeClockPunchResult,
): Promise<boolean> {
	const db = await getDb();
	const tx = db.transaction(STORE_NAME, 'readwrite');
	observeTransactionRejection(tx);
	const store = tx.objectStore(STORE_NAME);
	const record = await store.get(id);
	if (!record || record.punchKind !== 'machine' || !hasAsyncDocumentIdentity(record, source)) {
		await tx.done;
		return false;
	}
	await store.put({ ...record, machineResult: result });
	await tx.done;
	notify();
	return true;
}

// Retry 的 backoff 時間表。Index 對應下一次嘗試時的 retryCount，超過 length 後紀錄轉 `inactive`。
// Prod：1m / 5m / 15m / 30m / 1h / 3h / 6h（逐漸拉長，避免持續打 BE）
// Dev：1m / 2m / 3m / 4m / 5m / 6m / 7m（縮短間隔方便測試 retry 流程）
export const RETRY_BACKOFF_MS: number[] = isProd ?
	[
		1 * 60 * 1000,
		5 * 60 * 1000,
		15 * 60 * 1000,
		30 * 60 * 1000,
		60 * 60 * 1000,
		3 * 60 * 60 * 1000,
		6 * 60 * 60 * 1000,
	] :
	[
		1 * 60 * 1000,
		2 * 60 * 1000,
		3 * 60 * 1000,
		4 * 60 * 1000,
		5 * 60 * 1000,
		6 * 60 * 1000,
		7 * 60 * 1000,
	];

// 最大自動重試次數。達上限後紀錄轉 inactive，不再自動重送（仍可人工上傳）。
export const MAX_RETRY_COUNT = RETRY_BACKOFF_MS.length;

// 超過這個時間的紀錄會被 purge 掉，不管 status 是什麼，目前預設 7 天，。
export const TTL_MS = 7 * 24 * 60 * 60 * 1000;

// async retry 的 HTTP timeout 是 30s；lease 留 10s 給 callback / IDB finalizer。
// 到期接手仍使用同一把 idempotencyKey，因此 crash recovery 不會建立第二筆打卡。
export const ASYNC_RETRY_CLAIM_LEASE_MS = 40 * 1000;

// ============================================================
// IndexedDB 設定
// ============================================================

const DB_NAME = 'offline-punch-queue';
// 當 IndexedDB 的結構改動時要把這個版本號 +1，並在下面的 upgrade() 裡加對應的 migration：
//   - 新增 / 刪除 / 修改 object store
//   - 新增 / 刪除 / 重建 index
// 單純加欄位到 LocalQueueRecord 或改 TypeScript type 不需要動這個版本（IndexedDB 對 record 內容是 schemaless 的）
const DB_VERSION = 1;
const STORE_NAME = 'records';
const INDEX_BY_ACCOUNT_STATUS = 'by_account_status';
const INDEX_BY_CREATED = 'by_created';

interface OfflinePunchQueueSchema extends DBSchema {
	[STORE_NAME]: {
		key: string;
		value: LocalQueueRecord;
		indexes: {
			[INDEX_BY_ACCOUNT_STATUS]: [string, LocalQueueStatus];
			[INDEX_BY_CREATED]: number;
		};
	};
}

// Async singleton
// Cache 的是 Promise 而不是 resolved Database 實例，這樣多個 caller 同時呼叫 getDb() 時會共用同一個 pending Promise，確保只開一次 DB connection。
let dbPromise: Promise<IDBPDatabase<OfflinePunchQueueSchema>> | null = null;

// 寫入事件訂閱：每次 enqueue / update / remove / claim / markFailedRetry / purgeExpired 後通知 listener。
// 用途是讓 UI（hr/index.tsx 的 optimistic 顯示）跟 IDB 內容自動同步，不需要呼叫端記得 dispatch。
// 已知限制：notify 只在本行程（tab）內，無 BroadcastChannel / storage event——別的 tab 不會刷新
const listeners = new Set<() => void>();
function notify(): void {
	listeners.forEach(listener => {
		try { listener(); } catch (e) { /* listener 自己的錯誤不該影響 IDB 寫入 */ }
	});
}

// transaction completion reject 後，durable wrapper 可能只能靠 read-back 確認「其實已 commit」。
// 那條路沒有走到原 operation 尾端的 notify，必須顯式喚醒本 tab 的 UI / Firestore watcher。
export function notifyQueueSubscribers(): void {
	notify();
}

export function subscribe(listener: () => void): () => void {
	listeners.add(listener);
	return () => { listeners.delete(listener); };
}

function getDb(): Promise<IDBPDatabase<OfflinePunchQueueSchema>> {
	if (dbPromise) return dbPromise;
	let recoverable: Promise<IDBPDatabase<OfflinePunchQueueSchema>>;
	const invalidateConnection = (): void => {
		// 只清掉這次 open 所建立的 singleton，避免舊 connection 的 callback 誤清
		// 已由另一個 caller 建好的新 connection。
		if (dbPromise === recoverable) dbPromise = null;
		recoverable?.then(db => db.close())['catch'](() => { /* connection 已終止 */ });
	};
	const opening = openDB<OfflinePunchQueueSchema>(DB_NAME, DB_VERSION, {
		upgrade(db) {
			if (!db.objectStoreNames.contains(STORE_NAME)) {
				const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });

				store.createIndex(INDEX_BY_ACCOUNT_STATUS, ['accountId', 'status']);
				store.createIndex(INDEX_BY_CREATED, 'createdAt');
			}
		},
		// future schema upgrade 不能被本 bundle 的 cached connection 永久卡住。
		blocking: invalidateConnection,
		// Safari / WebView 可能在背景直接終止 connection；下次操作必須重新 open。
		terminated: invalidateConnection,
	});
	// open 本身若暫時失敗，不可永久 cache rejected Promise；scheduler 的 storage backoff
	// 下一輪必須真的重新 open，否則只能 reload 才恢復。
	recoverable = opening['catch'](error => {
		if (dbPromise === recoverable) dbPromise = null;
		throw error;
	});
	dbPromise = recoverable;
	return dbPromise;
}

// 清掉 cached promise，下次 getDb() 會重新 open 一次。
export function resetDbForTest(): void {
	dbPromise = null;
	listeners.clear();
}

// idb 在建立 transaction 時就產生獨立的 done Promise。request 若先 reject，caller 會在
// await store.get / put 處離開，來不及執行後面的 await tx.done；先觀察 rejection，避免它
// 變成全域 unhandledrejection。沒有替換原 Promise，所以正常流程 await tx.done 仍會
// 拋出原本的 completion error，durable wrapper 的 retry / read-back 判斷保持不變。
function observeTransactionRejection(transaction: { done: Promise<void> }): void {
	transaction.done['catch'](() => { /* 原 request / completion error 仍由操作本身傳回 caller */ });
}

// ============================================================
// CRUD
// ============================================================

type DistributiveOmit<T, K extends keyof T> = T extends unknown ? Omit<T, K> : never;

// 呼叫 enqueue() 時要傳的參數型別，比對一下 LocalQueueRecord：
//   - `id` 不用傳，由 enqueue 內部產生 uuid
//   - `status` 不用傳（或只能傳 'syncing'），一律強制寫 'syncing'
export type EnqueueInput = DistributiveOmit<LocalQueueRecord, 'id' | 'status'> & {
	status?: Extract<LocalQueueStatus, 'syncing'>;
};

// 寫入一筆新的 syncing 紀錄，回傳產生的 uuid。
// 呼叫端拿到 id 之後，在 API 回來時用這個 id 去做 update（轉 pending）或 remove（成功 / 4xx）。
export async function enqueue(input: EnqueueInput, requestedId: string = uuidv4()): Promise<string> {
	const db = await getDb();
	const record: LocalQueueRecord = {
		...input,
		id: requestedId,
		status: 'syncing',
	};
	await db.put(STORE_NAME, record);
	notify();
	return requestedId;
}

export type DurableEnqueueResult =
	| { result: 'stored'; id: string }
	| { result: 'not_stored' | 'unknown'; id: string };

// requestedId 是 durable retry 的 ownership token。第一次 put 可能已 commit、同 id 的 record 又被
// 另一個 tab 推進成 pending / accepted；重試只能確認它仍是同一個 owner，絕不可拿初始 snapshot 覆寫。
export function isSameDurableEnqueueOwner(
	existing: LocalQueueRecord | undefined,
	intended: LocalQueueRecord,
): boolean {
	return !!existing &&
		existing.id === intended.id &&
		existing.accountId === intended.accountId &&
		existing.createdAt === intended.createdAt &&
		existing.punchKind === intended.punchKind &&
		existing.idempotencyKey === intended.idempotencyKey;
}

export function decideDurableEnqueueReadback(
	existing: LocalQueueRecord | undefined,
	intended: LocalQueueRecord,
): Extract<DurableEnqueueResult['result'], 'stored' | 'unknown'> {
	return isSameDurableEnqueueOwner(existing, intended) ? 'stored' : 'unknown';
}

async function storeEnqueueRecordIfAbsent(
	db: IDBPDatabase<OfflinePunchQueueSchema>,
	record: LocalQueueRecord,
): Promise<'stored' | 'conflict'> {
	const tx = db.transaction(STORE_NAME, 'readwrite');
	observeTransactionRejection(tx);
	const store = tx.objectStore(STORE_NAME);
	const existing = await store.get(record.id);
	if (existing) {
		await tx.done;
		if (!isSameDurableEnqueueOwner(existing, record)) return 'conflict';
		// 可能是前一次 ambiguous commit，亦可能已被另一 tab 推進；只 refresh subscribers，不覆寫。
		notify();
		return 'stored';
	}
	await store.add(record);
	await tx.done;
	notify();
	return 'stored';
}

// IDB completion 可能在 commit 邊界 reject。caller 先固定 id；每次 transaction 都先 get，
// 已存在就視為 stored 且不覆寫（它可能已被另一 tab 推進），只有 absent 才 add 初始 record。
// 兩次都 reject 時 read-back 僅在「確定是同 owner」才回 stored；read 本身也失敗則 unknown。
export async function enqueueDurably(
	input: EnqueueInput,
	requestedId: string = uuidv4(),
): Promise<DurableEnqueueResult> {
	let db: IDBPDatabase<OfflinePunchQueueSchema>;
	try {
		db = await getDb();
	} catch {
		try {
			db = await getDb();
		} catch {
			// DB 連線都未取得，尚未進 put，不存在 commit ambiguity，可安全 fallback 同步 HTTP。
			return { result: 'not_stored', id: requestedId };
		}
	}
	const record: LocalQueueRecord = {
		...input,
		id: requestedId,
		status: 'syncing',
	};
	try {
		const result = await storeEnqueueRecordIfAbsent(db, record);
		if (result === 'conflict') return { result: 'unknown', id: requestedId };
		return { result: 'stored', id: requestedId };
	} catch {
		try {
			const result = await storeEnqueueRecordIfAbsent(db, record);
			if (result === 'conflict') return { result: 'unknown', id: requestedId };
			return { result: 'stored', id: requestedId };
		} catch {
			try {
				const existing = await db.get(STORE_NAME, requestedId);
				// 任一 write 已進入 ambiguous reject 後，absent 也可能是「已 commit，隨即被另一 tab
				// claim / terminal delete」；不可宣稱從未 stored 後 fallback 送無 owner 的第二發。
				if (decideDurableEnqueueReadback(existing, record) !== 'stored') {
					return { result: 'unknown', id: requestedId };
				}
				// ambiguous commit 已確認；補本 tab 的 in-memory subscriber 通知。
				notify();
				return { result: 'stored', id: requestedId };
			} catch {
				return { result: 'unknown', id: requestedId };
			}
		}
	}
}

// 更新一筆紀錄的部分欄位
// patch 只能包含「可以改的欄位」，不可以改的欄位用 TypeScript `Omit` 從 patch 型別裡排除：
//   - id / accountId：改了會破壞索引，也會讓這筆紀錄變成另一筆紀錄，不允許修改
//   - payload：原始打卡內容不該被事後改動，改了等於偽造紀錄
//   - createdAt：這是「使用者按下打卡的時間」，唯讀語意
//   - punchedAt / recognizeDate：必須透過專用 transaction 以同一個 rollover snapshot 成對更新
//   - rolloverSnapshot：enqueue 當下的歸屬日規則快照，不可事後換規則
//   - displayLocationName / displayShiftName：enqueue 當下的顯示快照，同 payload 不該事後改動
//   - punchKind：紀錄種類（一般 / 打卡機）決定 retry 端點，寫入後不該變
type ImmutableFields =
	| 'id' | 'accountId' | 'payload' | 'createdAt' | 'punchedAt' | 'recognizeDate'
	| 'rolloverSnapshot' | 'queueRecordVersion' | 'republishLease' | 'asyncRetryClaim'
	| 'displayLocationName' | 'displayShiftName' | 'allowPunchInOutOfRange' | 'punchKind';
type UpdatableFields = Omit<LocalQueueRecord, ImmutableFields>;

// punchedAt 與 recognizeDate 是同一個時間事實的兩種表示。新紀錄有 rolloverSnapshot 時
// 一律一起更新；舊紀錄沒有 snapshot 時，不能拿新 punchedAt 配舊 recognizeDate。
// 唯一安全的 legacy 例外是把 punchedAt 設回 createdAt：舊 recognizeDate 本來就是
// 用 createdAt 算出的，因此首次補值或 AC2 republish 由 server time 還原 click time 都一致。
export function withPunchTimePair(
	record: LocalQueueRecord,
	punchedAt: number,
): LocalQueueRecord {
	if (record.punchKind === 'machine') return { ...record, punchedAt };
	if (record.rolloverSnapshot) {
		const {
			isRolloverPreviousDay,
			dailyRolloverHour,
			dailyRolloverMinute,
		} = record.rolloverSnapshot;
		return {
			...record,
			punchedAt,
			recognizeDate: getRecognizeDateFromTime(
				createZonedMoment(record.rolloverSnapshot.timezone)(punchedAt),
				isRolloverPreviousDay,
				dailyRolloverHour,
				dailyRolloverMinute,
			).format('YYYY-MM-DD'),
		};
	}

	if (record.punchedAt === punchedAt) return record;
	if (punchedAt === record.createdAt) {
		return { ...record, punchedAt };
	}
	return record;
}

export async function update(
	id: string,
	patch: Partial<UpdatableFields>,
): Promise<void> {
	const db = await getDb();
	const tx = db.transaction(STORE_NAME, 'readwrite');
	observeTransactionRejection(tx);
	const store = tx.objectStore(STORE_NAME);
	const existing = await store.get(id);
	if (!existing) {
		await tx.done;
		return;
	}
	await store.put({ ...existing, ...patch });
	await tx.done;
	notify();
}

export type ArmForegroundRequestResult =
	| { result: 'updated'; requestStartedAt: number }
	| { result: 'skipped' | 'not_found' };

export type ArmForegroundRequestDecision =
	| { result: 'updated'; record: LocalQueueRecord; requestStartedAt: number }
	| { result: 'skipped' | 'not_found' };

// effective reclaim 門檻是 39s、HTTP timeout 是 30s；dispatch 時 arm 最多只能舊 5s，
// 保留至少 4s 給 timer 誤差與 event-loop 排程。若 IDB commit / 分頁 freeze 讓它更舊，
// caller 必須用相同 owner 再 arm，或放棄前景 HTTP 交給 scheduler 回收。
export const FOREGROUND_REQUEST_ARM_MAX_AGE_MS = 5 * 1000;

export function isForegroundRequestArmFresh(
	requestStartedAt: number,
	now: number = Date.now(),
): boolean {
	return Number.isFinite(requestStartedAt) &&
		now - requestStartedAt <= FOREGROUND_REQUEST_ARM_MAX_AGE_MS;
}

// Dispatch 前的 durable arm。transaction guard 確保等待 enqueue 的 continuation 恢復時，
// 不會把已被 202 / error / 其他 tab 接手的紀錄重新武裝後再送一發。
export function decideForegroundRequestArm(
	existing: LocalQueueRecord | undefined,
	idempotencyKey: string,
	now: number,
): ArmForegroundRequestDecision {
	if (!existing) return { result: 'not_found' };
	if (
		!isInitialForegroundRequest(existing, idempotencyKey)
	) {
		return { result: 'skipped' };
	}
	return {
		result: 'updated',
		requestStartedAt: now,
		record: { ...existing, requestStartedAt: now },
	};
}

export async function armForegroundRequest(
	id: string,
	idempotencyKey: string,
	now?: number,
): Promise<ArmForegroundRequestResult> {
	const db = await getDb();
	const tx = db.transaction(STORE_NAME, 'readwrite');
	observeTransactionRejection(tx);
	const store = tx.objectStore(STORE_NAME);
	const existing = await store.get(id);
	// production timestamp 必須在可能被 IDB block 的 get 之後才取。若用 default parameter，
	// Date.now() 會在函式進入時就求值，transaction 等 20s 後仍會寫入舊 baseline。
	// `now` 只留給 deterministic test；production caller 省略。
	const armedAt = now ?? Date.now();
	const decision = decideForegroundRequestArm(existing, idempotencyKey, armedAt);
	if (decision.result !== 'updated') {
		await tx.done;
		return decision;
	}
	await store.put(decision.record);
	await tx.done;
	notify();
	return { result: 'updated', requestStartedAt: decision.requestStartedAt };
}

// 刪除一筆紀錄。如果 id 不存在會靜默通過（idb 的 delete 對不存在的 key 不會 throw error）。
export async function remove(id: string): Promise<void> {
	const db = await getDb();
	await db.delete(STORE_NAME, id);
	notify();
}

export interface RetryClaimOptions {
	claimId?: string;
	now?: number;
}

export type RetryClaimDecision =
	| { result: 'claimed'; record: LocalQueueRecord }
	| { result: 'not_claimable' };

function withRetryClaim(
	record: LocalQueueRecord,
	claimId: string,
	now: number,
): LocalQueueRecord {
	const syncing: LocalQueueRecord = { ...record, status: 'syncing' };
	if (!record.idempotencyKey) return syncing;
	return {
		...syncing,
		asyncRetryClaim: {
			claimId,
			expiresAt: now + ASYNC_RETRY_CLAIM_LEASE_MS,
		},
	};
}

// 純決策函式供 race / crash recovery 測試。只有 async record 會取得 persisted fencing token；
// sync / 無 key 的舊 machine 沿用原本無自動回收的 syncing 行為。
export function decideRetryClaim(
	existing: LocalQueueRecord | undefined,
	claimId: string,
	now: number,
): RetryClaimDecision {
	if (!existing || existing.status !== 'pending' || hasOutstandingAsyncDocument(existing)) {
		return { result: 'not_claimable' };
	}
	return { result: 'claimed', record: withRetryClaim(existing, claimId, now) };
}

// 防多 tab race：兩個 scheduler 同時撿同一筆時只有一個能 claim 成功。async claim 另帶
// persisted lease；若 claimant 關頁，lease 到期後會回到 pending 並以同一 idempotencyKey 接手。
export async function claimForRetry(
	id: string,
	options: RetryClaimOptions = {},
): Promise<LocalQueueRecord | null> {
	const db = await getDb();
	const tx = db.transaction(STORE_NAME, 'readwrite');
	observeTransactionRejection(tx);
	const store = tx.objectStore(STORE_NAME);
	const existing = await store.get(id);
	const decision = decideRetryClaim(
		existing,
		options.claimId ?? uuidv4(),
		options.now ?? Date.now(),
	);
	if (decision.result !== 'claimed') {
		await tx.done;
		return null;
	}
	await store.put(decision.record);
	await tx.done;
	notify();
	return decision.record;
}

export type ManualRetryClaimResult =
	| { result: 'claimed'; record: LocalQueueRecord }
	| { result: 'not_found' };

export function decideManualRetryClaim(
	record: LocalQueueRecord | undefined,
	accountId: string,
): 'claimable' | 'not_found' {
	if (!record || record.accountId !== accountId || hasOutstandingAsyncDocument(record)) return 'not_found';
	return 'claimable';
}

export type ManualRetryClaimDecision =
	| { result: 'claimed'; record: LocalQueueRecord }
	| { result: 'not_found' };

export function decideManualRetryClaimRecord(
	record: LocalQueueRecord | undefined,
	accountId: string,
	claimId: string,
	now: number,
): ManualRetryClaimDecision {
	if (decideManualRetryClaim(record, accountId) === 'not_found') return { result: 'not_found' };
	return {
		result: 'claimed',
		record: {
			...withRetryClaim(record as LocalQueueRecord, claimId, now),
			nextRetryAt: now,
		},
	};
}

// 手動上傳是使用者明確要求「現在再送一次」：pending 略過 backoff、inactive 可人工救援，
// syncing 也允許強制重送，避免前次請求因分頁中斷而永久卡死。這代表它可能和正在進行的
// 自動補送重疊（同步 / machine 無冪等 key，產品已接受可能重複）；transaction 的用途是原子
// 取得最新紀錄並維持 syncing，不讓 manual claim 自己製造一個可被 scheduler 額外撿走的 pending 空窗。
export async function claimForManualRetry(
	id: string,
	accountId: string,
	options: RetryClaimOptions = {},
): Promise<ManualRetryClaimResult> {
	const db = await getDb();
	const tx = db.transaction(STORE_NAME, 'readwrite');
	observeTransactionRejection(tx);
	const store = tx.objectStore(STORE_NAME);
	const existing = await store.get(id);
	const decision = decideManualRetryClaimRecord(
		existing,
		accountId,
		options.claimId ?? uuidv4(),
		options.now ?? Date.now(),
	);
	if (decision.result === 'not_found') {
		await tx.done;
		return { result: 'not_found' };
	}
	await store.put(decision.record);
	await tx.done;
	notify();
	return decision;
}

export type DurableManualRetryClaimResult =
	| ManualRetryClaimResult
	| { result: 'superseded' | 'unknown' };

export function isManualAsyncRetryClaimApplied(
	record: LocalQueueRecord | undefined,
	accountId: string,
	claimId: string,
): boolean {
	return !!record &&
		record.accountId === accountId &&
		!!record.idempotencyKey &&
		record.status === 'syncing' &&
		!!record.idempotencyKey &&
		record.asyncRetryClaim?.claimId === claimId &&
		record.docKey == null &&
		record.asyncStatus == null &&
		record.republishLease == null;
}

// 手動 claim 的 transaction completion 也可能在 commit 後 reject。單一 logical attempt 固定
// claimId + lease baseline 重試；兩次都 reject 時只在 readback 看見同 claim owner 才允許送 HTTP。
// absent / 無法讀取一律 unknown，交 scheduler storage backoff，不能猜測 claim 從未 commit。
export async function claimForManualRetryDurably(
	id: string,
	accountId: string,
): Promise<DurableManualRetryClaimResult> {
	const stableClaimId = uuidv4();
	const stableOptions: RetryClaimOptions = { claimId: stableClaimId, now: Date.now() };
	try {
		return await claimForManualRetry(id, accountId, stableOptions);
	} catch {
		try {
			const retried = await claimForManualRetry(id, accountId, stableOptions);
			if (retried.result === 'claimed') notify();
			return retried;
		} catch {
			try {
				const latest = await getById(id);
				if (isManualAsyncRetryClaimApplied(
					latest,
					accountId,
					stableClaimId,
				)) {
					notify();
					return { result: 'claimed', record: latest };
				}
				if (latest?.asyncRetryClaim?.claimId) {
					notify();
					return { result: 'superseded' };
				}
				notify();
				return { result: 'unknown' };
			} catch {
				notify();
				return { result: 'unknown' };
			}
		}
	}
}

// retry 失敗後 retryCount++ 並排下次 nextRetryAt；達 MAX_RETRY_COUNT 標 inactive，等 TTL 清掉。
export async function markFailedRetry(id: string, lastError: string): Promise<void> {
	const db = await getDb();
	const tx = db.transaction(STORE_NAME, 'readwrite');
	observeTransactionRejection(tx);
	const store = tx.objectStore(STORE_NAME);
	const existing = await store.get(id);
	if (!existing) {
		await tx.done;
		return;
	}
	const newRetryCount = existing.retryCount + 1;
	const next: LocalQueueRecord = newRetryCount >= MAX_RETRY_COUNT ?
		{ ...existing, status: 'inactive', retryCount: newRetryCount, lastError } :
		{
			...existing,
			status: 'pending',
			retryCount: newRetryCount,
			nextRetryAt: Date.now() + RETRY_BACKOFF_MS[newRetryCount],
			lastError,
		};
	await store.put(next);
	await tx.done;
	notify();
}

// ============================================================
// Scheduler async /enqueue 的 guarded local finalizers
// ============================================================

export interface AsyncQueueRetrySource {
	idempotencyKey: string;
	retryCount: number;
	claimId: string;
}

function isSameAsyncQueueRetryOwner(
	record: LocalQueueRecord,
	source: AsyncQueueRetrySource,
): boolean {
	return !!record.idempotencyKey &&
		record.status === 'syncing' &&
		record.idempotencyKey === source.idempotencyKey &&
		record.retryCount === source.retryCount &&
		record.asyncRetryClaim?.claimId === source.claimId &&
		record.docKey == null &&
		record.asyncStatus == null &&
		record.republishLease == null;
}

export interface FinalizeAsyncQueueAcceptedOptions {
	source: AsyncQueueRetrySource;
	docKey: string;
}

export type FinalizeAsyncQueueAcceptedResult = 'accepted' | 'superseded' | 'not_found';

export function isAsyncQueueAcceptedApplied(
	record: LocalQueueRecord | undefined,
	options: FinalizeAsyncQueueAcceptedOptions,
): boolean {
	return !!record &&
		!!record.idempotencyKey &&
		record.status === 'syncing' &&
		record.idempotencyKey === options.source.idempotencyKey &&
		record.retryCount === options.source.retryCount &&
		record.asyncRetryClaim?.claimId === options.source.claimId &&
		record.docKey === options.docKey &&
		record.asyncStatus != null;
}

export type FinalizeAsyncQueueAcceptedDecision =
	| { result: 'accepted'; record: LocalQueueRecord }
	| { result: 'superseded' | 'not_found' };

export function decideFinalizeAsyncQueueAccepted(
	existing: LocalQueueRecord | undefined,
	options: FinalizeAsyncQueueAcceptedOptions,
): FinalizeAsyncQueueAcceptedDecision {
	if (!existing) return { result: 'not_found' };
	if (isAsyncQueueAcceptedApplied(existing, options)) return { result: 'accepted', record: existing };
	if (!isSameAsyncQueueRetryOwner(existing, options.source)) return { result: 'superseded' };
	return {
		result: 'accepted',
		record: {
			...existing,
			docKey: options.docKey,
			asyncStatus: 'accepted',
			serverAcceptedAt: existing.serverAcceptedAt ?? Date.now(),
			allowResultModal: false,
		},
	};
}

export async function finalizeAsyncQueueAccepted(
	id: string,
	options: FinalizeAsyncQueueAcceptedOptions,
): Promise<FinalizeAsyncQueueAcceptedResult> {
	const db = await getDb();
	const tx = db.transaction(STORE_NAME, 'readwrite');
	observeTransactionRejection(tx);
	const store = tx.objectStore(STORE_NAME);
	const existing = await store.get(id);
	const decision = decideFinalizeAsyncQueueAccepted(existing, options);
	if (!('record' in decision)) {
		await tx.done;
		return decision.result;
	}
	await store.put(decision.record);
	await tx.done;
	notify();
	return 'accepted';
}

export interface FinalizeAsyncQueueRetryFailureOptions {
	source: AsyncQueueRetrySource;
	lastError: string;
	now?: number;
}

export type FinalizeAsyncQueueRetryFailureResult =
	| 'scheduled' | 'exhausted' | 'superseded' | 'not_found';

export function getAppliedAsyncQueueRetryFailureResult(
	record: LocalQueueRecord | undefined,
	options: FinalizeAsyncQueueRetryFailureOptions,
): Extract<FinalizeAsyncQueueRetryFailureResult, 'scheduled' | 'exhausted'> | undefined {
	if (!record ||
		!record.idempotencyKey ||
		record.idempotencyKey !== options.source.idempotencyKey ||
		record.retryCount !== options.source.retryCount + 1 ||
		record.asyncRetryClaim?.claimId !== options.source.claimId ||
		record.docKey != null ||
		record.asyncStatus != null ||
		record.republishLease != null
	) return undefined;
	if (record.status === 'pending') return 'scheduled';
	if (record.status === 'inactive') return 'exhausted';
	return undefined;
}

export type FinalizeAsyncQueueRetryFailureDecision =
	| { result: 'scheduled' | 'exhausted'; record: LocalQueueRecord }
	| { result: 'superseded' | 'not_found' };

export function decideFinalizeAsyncQueueRetryFailure(
	existing: LocalQueueRecord | undefined,
	options: FinalizeAsyncQueueRetryFailureOptions,
	now: number,
): FinalizeAsyncQueueRetryFailureDecision {
	if (!existing) return { result: 'not_found' };
	const applied = getAppliedAsyncQueueRetryFailureResult(existing, options);
	if (applied) return { result: applied, record: existing };
	if (!isSameAsyncQueueRetryOwner(existing, options.source)) return { result: 'superseded' };

	const retryCount = existing.retryCount + 1;
	if (retryCount >= MAX_RETRY_COUNT) {
		return {
			result: 'exhausted',
			record: { ...existing, status: 'inactive', retryCount, lastError: options.lastError },
		};
	}
	return {
		result: 'scheduled',
		record: {
			...existing,
			status: 'pending',
			retryCount,
			nextRetryAt: now + RETRY_BACKOFF_MS[retryCount],
			lastError: options.lastError,
		},
	};
}

export async function finalizeAsyncQueueRetryFailure(
	id: string,
	options: FinalizeAsyncQueueRetryFailureOptions,
): Promise<FinalizeAsyncQueueRetryFailureResult> {
	const db = await getDb();
	const tx = db.transaction(STORE_NAME, 'readwrite');
	observeTransactionRejection(tx);
	const store = tx.objectStore(STORE_NAME);
	const existing = await store.get(id);
	const decision = decideFinalizeAsyncQueueRetryFailure(existing, options, options.now ?? Date.now());
	if (!('record' in decision)) {
		await tx.done;
		return decision.result;
	}
	await store.put(decision.record);
	await tx.done;
	notify();
	return decision.result;
}

export interface FinalizeAsyncQueueFatalOptions {
	source: AsyncQueueRetrySource;
}

export function decideFinalizeAsyncQueueFatal(
	existing: LocalQueueRecord | undefined,
	options: FinalizeAsyncQueueFatalOptions,
): 'removed' | 'superseded' | 'not_found' {
	if (!existing) return 'not_found';
	return isSameAsyncQueueRetryOwner(existing, options.source) ? 'removed' : 'superseded';
}

export async function finalizeAsyncQueueFatal(
	id: string,
	options: FinalizeAsyncQueueFatalOptions,
): Promise<'removed' | 'superseded' | 'not_found'> {
	const db = await getDb();
	const tx = db.transaction(STORE_NAME, 'readwrite');
	observeTransactionRejection(tx);
	const store = tx.objectStore(STORE_NAME);
	const existing = await store.get(id);
	const decision = decideFinalizeAsyncQueueFatal(existing, options);
	if (decision !== 'removed') {
		await tx.done;
		return decision;
	}
	await store.delete(id);
	await tx.done;
	notify();
	return 'removed';
}

// 根據 id 拿單一筆紀錄。找不到回 undefined。
export async function getById(id: string): Promise<LocalQueueRecord | undefined> {
	const db = await getDb();
	return db.get(STORE_NAME, id);
}

// 回傳某個帳號所有紀錄（不分 status）
export async function getAllByAccount(accountId: string): Promise<LocalQueueRecord[]> {
	const db = await getDb();
	const all = await db.getAll(STORE_NAME);
	return all.filter(record => record.accountId === accountId);
}

// 回傳某個帳號、指定 status 的所有紀錄
export async function getByStatus(
	accountId: string,
	status: LocalQueueStatus,
): Promise<LocalQueueRecord[]> {
	const db = await getDb();
	return db.getAllFromIndex(STORE_NAME, INDEX_BY_ACCOUNT_STATUS, [accountId, status]);
}

export function isOutstandingAsyncStatus(status?: PunchTaskStatus): boolean {
	return ['accepted', 'processing', 'published'].includes(status);
}

// Queue 的儲存狀態（status）只描述目前由誰持有紀錄；UI 還需要結合 asyncStatus、
// deferredReason 與紀錄種類，才能判斷使用者實際看到的階段。所有 consumer 應使用這個
// selector，避免各自用「尚未 succeeded」之類的負面條件把 failed 誤判成仍在等待。
export type QueuePhase =
	| 'awaiting_async_result'
	| 'retrying'
	| 'succeeded'
	| 'failed'
	| 'inactive'
	| 'in_flight';

export function getQueuePhase(record: LocalQueueRecord): QueuePhase {
	if (record.asyncStatus === 'failed') return 'failed';
	if (record.asyncStatus === 'succeeded') return 'succeeded';

	if (record.punchKind === 'machine' && record.docKey && isOutstandingAsyncStatus(record.asyncStatus)) {
		return 'awaiting_async_result';
	}
	const isAwaitingAsyncResult = !!record.idempotencyKey &&
		record.status === 'syncing' &&
		!record.deferredReason &&
		(record.asyncStatus == null || isOutstandingAsyncStatus(record.asyncStatus));
	if (isAwaitingAsyncResult) return 'awaiting_async_result';

	if (record.status === 'inactive') return 'inactive';
	if (record.deferredReason) return 'retrying';
	return 'in_flight';
}

export function isForegroundWaiting(record: LocalQueueRecord): boolean {
	return getQueuePhase(record) === 'awaiting_async_result';
}

// Initial /enqueue HTTP ownership guard。UI 的 isForegroundWaiting 刻意也涵蓋
// accepted / processing / published，不能拿來判斷「原始前景 request 是否仍可收尾」。
// 所有 late onSuccess / onError / fatal delete / stale reclaim 都必須用這支並比對原 key。
export function isInitialForegroundRequest(
	record: LocalQueueRecord,
	idempotencyKey?: string,
): boolean {
	return !!record.idempotencyKey &&
		record.queueRecordVersion === CURRENT_QUEUE_RECORD_VERSION &&
		record.status === 'syncing' &&
		!record.deferredReason &&
		!!record.idempotencyKey &&
		(idempotencyKey == null || record.idempotencyKey === idempotencyKey) &&
		record.docKey == null &&
		record.asyncStatus == null &&
		record.republishLease == null &&
		record.asyncRetryClaim == null;
}

// 一般與卡機 server queue 共用監聽；舊卡機紀錄沒有 key/doc，不會被撿入。
export async function getOutstandingAsync(accountId: string): Promise<LocalQueueRecord[]> {
	const all = await getAllByAccount(accountId);
	return all.filter(record => (
		!!record.idempotencyKey && !!record.docKey && isOutstandingAsyncStatus(record.asyncStatus)
	));
}

// 舊 bundle 曾可能留下 pending + docKey + accepted 的 hybrid。只要完整 doc ownership 還在，
// watcher 優先於 scheduler；watcher 的 guarded transaction 會把 status 原子 normalize 回 syncing。
export function hasOutstandingAsyncDocument(record: LocalQueueRecord): boolean {
	return !!record.idempotencyKey &&
		!!record.idempotencyKey &&
		!!record.docKey &&
		isOutstandingAsyncStatus(record.asyncStatus);
}

// UI interaction lock 專用：包含尚未拿到 202/docKey 的 initial foreground record，
// 也包含 accepted / processing / published。不要拿它取代 getOutstandingAsync：watcher 只有
// docKey 已知時才能訂閱 Firestore。
export async function getPendingAsyncInteractions(accountId: string): Promise<RegularQueueRecord[]> {
	const all = await getAllByAccount(accountId);
	return all.filter((record): record is RegularQueueRecord => (
		record.punchKind !== 'machine' && !!record.idempotencyKey && isForegroundWaiting(record)
	));
}

// ============================================================
// Scheduler helpers
// ============================================================

// 從 pending 清單裡找出「nextRetryAt 最早的那一筆」。
// 這個用途是給 dynamic scheduler 用：scheduler 用 setTimeout 在指定時間醒來 tick，而「指定時間」就是這筆最早要 retry 的 record 的 nextRetryAt。沒有 pending 就不醒。
export async function getNextPending(
	accountId: string,
): Promise<LocalQueueRecord | undefined> {
	const pending = (await getByStatus(accountId, 'pending')).filter(record => !hasOutstandingAsyncDocument(record));
	if (pending.length === 0) return undefined;
	return pending.reduce((earliest, current) => (
		current.nextRetryAt < earliest.nextRetryAt ? current : earliest
	));
}

// 從 pending 清單裡找出「現在該 retry 的 record」（nextRetryAt <= now）。
// Scheduler tick 醒來時會呼叫這個拿一批可以送的 record，然後一筆一筆嘗試送出。
// 注意這裡可能一次拿到多筆（例如多筆 nextRetryAt 同時到期、或 scheduler 被 throttle 後一次醒來），呼叫端要準備好處理 array，不只是一筆。
export async function listReadyForRetry(
	accountId: string,
	now: number,
): Promise<LocalQueueRecord[]> {
	const pending = await getByStatus(accountId, 'pending');
	return pending.filter(record => !hasOutstandingAsyncDocument(record) && record.nextRetryAt <= now);
}

// ============================================================
// 進入 retry 準備區（唯一入口）
// ============================================================

export type EnterRetryQueueResult = 'moved' | 'already_in_queue' | 'skipped' | 'not_found';

export interface EnterRetryQueueOptions {
	// 這次為什麼延後送出（如 'http_502' / 'timeout' / 'stale_syncing_reclaim'）。
	// 必填不是為了 BE 歸因，是為了讓「已進補送鏈」與「有 deferredReason」永遠等價，見下方註解。
	// 紀錄已有原因就保留舊的：第一次轉 pending 的歸因才是 BE 要的。
	deferredReason: string;
	// 補送要記的打卡時間，預設沿用紀錄現有的 punchedAt（再退回 createdAt）
	punchedAt?: number;
	// 下次重送時間，預設「立刻」（本輪 scan 的 listReadyForRetry 就撿得到）
	nextRetryAt?: number;
	// 重試次數歸零：前景第一次失敗轉背景補送時用（這是這筆的第一輪 retry）
	resetRetryCount?: boolean;
	// 一併寫入的欄位，如 republish 換上新 idempotencyKey、清掉舊 docKey / asyncStatus
	extra?: Partial<Pick<BaseQueueRecord, 'idempotencyKey' | 'docKey' | 'asyncStatus' | 'allowResultModal'>>;
	// transaction 內重判：讀出候選到真正寫入之間狀態可能已變（如 202 剛回來押上 docKey）。回 false 就不動這筆
	guard?: (record: LocalQueueRecord) => boolean;
}

// 把一筆 in-flight 紀錄交棒給 scheduler 背景補送。**所有「進入 retry 準備區」的路徑都要走這支。**
//
// 為什麼要收斂成單一入口：補送路徑一律帶 punch_datetime（見 offlinePunchScheduler.buildRetryPayload
// 與 punchQueueClient.buildQueuePayload），也就是「這筆的 DB 時間會用前端押的 punchedAt，而不是 server
// 收到的時間」。UI 靠 deferredReason 判斷該不該在打卡時間旁標離線驚嘆號
// （見 hooks/punch/useOptimisticPunches.usesLocalPunchTime），所以「進了補送鏈」與「有 deferredReason」
// 必須永遠等價。過去各處自己拼 update({status:'pending', ...})，firestorePunchWatcher 的 republish
// 分支就漏掉 deferredReason，讓等價默默破掉。這支把 status / punchedAt / deferredReason 綁在一起寫、
// deferredReason 設成必填參數，呼叫端不可能只做一半。
//
// 回傳值語意：
//   moved            → 本次由這支移入補送鏈
//   already_in_queue → 先前已被移入（別的分頁、或 reclaimStaleSyncing 搶先）；不覆蓋它排好的 nextRetryAt / retryCount
//   skipped          → guard 擋下（狀態在讀後寫之間變了）
//   not_found        → 紀錄已不存在，沒有東西會被補送
export type RetryQueueDecision =
	| { result: 'already_in_queue' | 'skipped' }
	| { result: 'moved'; record: LocalQueueRecord };

// enterRetryQueue 的決策與欄位組裝（純函式，供測試）。IDB 那層只負責 transaction 與寫入。
export function decideRetryQueueEntry(
	existing: LocalQueueRecord,
	options: EnterRetryQueueOptions,
	now: number,
): RetryQueueDecision {
	// 已被別的分頁 / reclaimStaleSyncing 移入補送鏈：不覆蓋它排好的 nextRetryAt / retryCount。
	// 注意這裡擋不掉「已進補送鏈、但正被 claimForRetry 鎖成 syncing」的紀錄——那要靠呼叫端傳
	// guard: isForegroundWaiting（watcher 的 republish 是合法的例外：它處理的是自己監聽中的紀錄）
	if (existing.status !== 'syncing') return { result: 'already_in_queue' };
	if (options.guard && !options.guard(existing)) return { result: 'skipped' };
	const punchedAt = options.punchedAt ?? existing.punchedAt ?? existing.createdAt;
	const moved: LocalQueueRecord = {
		...existing,
		...options.extra,
		status: 'pending',
		// 進入 pending 即釋放目前 in-flight fencing token；下一輪 claim 會建立新 token。
		asyncRetryClaim: undefined,
		deferredReason: existing.deferredReason ?? options.deferredReason,
		retryCount: options.resetRetryCount ? 0 : existing.retryCount,
		nextRetryAt: options.nextRetryAt ?? now,
	};
	return {
		result: 'moved',
		// 新格式同時重算 recognizeDate；legacy 僅允許安全的 createdAt 初始補值。
		record: withPunchTimePair(moved, punchedAt),
	};
}

export async function enterRetryQueue(
	id: string,
	options: EnterRetryQueueOptions,
): Promise<EnterRetryQueueResult> {
	const db = await getDb();
	const tx = db.transaction(STORE_NAME, 'readwrite');
	observeTransactionRejection(tx);
	const store = tx.objectStore(STORE_NAME);
	const existing = await store.get(id);
	if (!existing) {
		await tx.done;
		return 'not_found';
	}
	const decision = decideRetryQueueEntry(existing, options, Date.now());
	if (decision.result !== 'moved') {
		await tx.done;
		return decision.result;
	}
	await store.put(decision.record);
	await tx.done;
	notify();
	return 'moved';
}

export type DurableEnterRetryQueueResult = EnterRetryQueueResult | 'unknown';

// completion reject 可能發生在 commit 之後；以相同 options 重試只會得到 already_in_queue，
// 不會重複遞增任何計數。兩次 reject 後 read-back 明確區分已套用／被別人接手／不存在／未知。
export async function enterRetryQueueDurably(
	id: string,
	options: EnterRetryQueueOptions,
): Promise<DurableEnterRetryQueueResult> {
	try {
		const result = await enterRetryQueue(id, options);
		if (result === 'already_in_queue' || result === 'skipped') notify();
		return result;
	} catch {
		try {
			const retried = await enterRetryQueue(id, options);
			// already_in_queue / skipped 可能是在確認第一次其實已 commit；補本 tab refresh。
			notify();
			return retried;
		} catch {
			try {
				const existing = await getById(id);
				if (!existing) return 'not_found';
				if (existing.status !== 'syncing' || existing.deferredReason) {
					notify();
					return 'already_in_queue';
				}
				if (options.guard && !options.guard(existing)) {
					notify();
					return 'skipped';
				}
				return 'unknown';
			} catch {
				return 'unknown';
			}
		}
	}
}

// ============================================================
// 202 受理收尾（前景 onSuccess 專用）
// ============================================================

// 遲到的 202 是否仍可押 accepted（純函式，供測試）：紀錄仍是 initial foreground owner 才可。
// tab 凍結後恢復時，202 的 onSuccess 可能晚於 reclaimStaleSyncing——紀錄已被拉回 pending、寫上
// deferredReason，此時覆蓋會產生 pending + accepted 的混血（scheduler 與 watcher 同時認領），
// 且 punchedAt 會被改成 server 時間，與已承諾補送的 punch_datetime（= createdAt）脫鉤。
// 與 onError 撞 reclaim 時 enterRetryQueue 回 already_in_queue 不動它對稱。
// 注意：queue 出身紀錄由 scheduler 重送拿到 202 時「不」走這支（它有 deferredReason 會被擋）——
// 那條路是紀錄的持有者（claimForRetry 鎖定中），由 processQueueRecord 自行 update。
export function canMarkAccepted(record: LocalQueueRecord, idempotencyKey?: string): boolean {
	return isInitialForegroundRequest(record, idempotencyKey);
}

export interface MarkAcceptedOptions {
	idempotencyKey: string;
	docKey: string;
	punchedAt?: number;
}

export type MarkAcceptedResult = 'updated' | 'already_applied' | 'skipped' | 'not_found';
export type DurableMarkAcceptedResult = MarkAcceptedResult | 'unknown';

// already_applied 與 skipped 都代表 IDB 已由本次 ambiguous commit 或 successor owner 推進；
// durable wrapper 必須通知本 tab subscriber 重讀，否則 watcher / UI 可能停在舊 snapshot。
export function shouldNotifyMarkAcceptedSuccessor(result: MarkAcceptedResult): boolean {
	return result === 'already_applied' || result === 'skipped';
}

export function isMarkAcceptedApplied(
	record: LocalQueueRecord | undefined,
	options: MarkAcceptedOptions,
): boolean {
	return !!record &&
		!!record.idempotencyKey &&
		record.idempotencyKey === options.idempotencyKey &&
		record.docKey === options.docKey &&
		record.asyncStatus != null;
}

export type MarkAcceptedDecision =
	| { result: 'updated'; record: LocalQueueRecord }
	| { result: 'already_applied' | 'skipped' | 'not_found' };

export function decideMarkAccepted(
	existing: LocalQueueRecord | undefined,
	options: MarkAcceptedOptions,
): MarkAcceptedDecision {
	if (!existing) return { result: 'not_found' };
	if (isMarkAcceptedApplied(existing, options)) return { result: 'already_applied' };
	if (!canMarkAccepted(existing, options.idempotencyKey)) return { result: 'skipped' };
	const accepted: LocalQueueRecord = {
		...existing,
		asyncStatus: 'accepted',
		docKey: options.docKey,
		serverAcceptedAt: existing.serverAcceptedAt ?? Date.now(),
	};
	return {
		result: 'updated',
		record: options.punchedAt != null ? withPunchTimePair(accepted, options.punchedAt) : accepted,
	};
}

// 前景 /enqueue 202 的收尾：transaction 內以 canMarkAccepted 重判後才寫入 accepted + docKey。
// skipped 不會漏收尾：補送鏈以同 key 冪等重放 /enqueue，docKey / asyncStatus 由 scheduler 的 202 路徑補回。
export async function markAccepted(
	id: string,
	options: MarkAcceptedOptions,
): Promise<MarkAcceptedResult> {
	const db = await getDb();
	const tx = db.transaction(STORE_NAME, 'readwrite');
	observeTransactionRejection(tx);
	const store = tx.objectStore(STORE_NAME);
	const existing = await store.get(id);
	const decision = decideMarkAccepted(existing, options);
	if (decision.result !== 'updated') {
		await tx.done;
		if (decision.result === 'already_applied') notify();
		return decision.result;
	}
	await store.put(decision.record);
	await tx.done;
	notify();
	return 'updated';
}

// 202 local commit 的 completion reject 不可讓 watcher 永遠看不到 docKey。用相同 key/doc 重試；
// 兩次 reject 後只有 read-back 明確看見同 owner 的 accepted/進階狀態才視為 already_applied。
export async function markAcceptedDurably(
	id: string,
	options: MarkAcceptedOptions,
): Promise<DurableMarkAcceptedResult> {
	try {
		const result = await markAccepted(id, options);
		if (shouldNotifyMarkAcceptedSuccessor(result)) notify();
		return result;
	} catch {
		try {
			const retried = await markAccepted(id, options);
			if (shouldNotifyMarkAcceptedSuccessor(retried)) notify();
			return retried;
		} catch {
			try {
				const latest = await getById(id);
				if (isMarkAcceptedApplied(latest, options)) {
					notify();
					return 'already_applied';
				}
				if (!latest) return 'not_found';
				if (!canMarkAccepted(latest, options.idempotencyKey)) {
					notify();
					return 'skipped';
				}
				// 狀態仍可能由另一個 tab 推進；喚醒本 tab watcher / UI 再讀一次。
				notify();
				return 'unknown';
			} catch {
				notify();
				return 'unknown';
			}
		}
	}
}

export interface ForegroundFatalOptions {
	idempotencyKey: string;
}

export type ForegroundFatalResult = 'removed' | 'superseded' | 'not_found';
export type DurableForegroundFatalResult = ForegroundFatalResult | 'unknown';

export function decideFinalizeForegroundFatal(
	existing: LocalQueueRecord | undefined,
	options: ForegroundFatalOptions,
): ForegroundFatalResult {
	if (!existing) return 'not_found';
	return isInitialForegroundRequest(existing, options.idempotencyKey) ? 'removed' : 'superseded';
}

export async function finalizeForegroundFatal(
	id: string,
	options: ForegroundFatalOptions,
): Promise<ForegroundFatalResult> {
	const db = await getDb();
	const tx = db.transaction(STORE_NAME, 'readwrite');
	observeTransactionRejection(tx);
	const store = tx.objectStore(STORE_NAME);
	const existing = await store.get(id);
	const decision = decideFinalizeForegroundFatal(existing, options);
	if (decision !== 'removed') {
		await tx.done;
		return decision;
	}
	await store.delete(id);
	await tx.done;
	notify();
	return 'removed';
}

export async function finalizeForegroundFatalDurably(
	id: string,
	options: ForegroundFatalOptions,
): Promise<DurableForegroundFatalResult> {
	try {
		const result = await finalizeForegroundFatal(id, options);
		if (result === 'superseded') notify();
		return result;
	} catch {
		try {
			const retried = await finalizeForegroundFatal(id, options);
			// 第一次可能已刪除，也可能是另一個 owner 先收尾；沒有 tombstone 無法歸因，
			// 不可把 not_found 冒充成本 callback 的 removed 後發出 stale failure outcome。
			if (retried === 'not_found') notify();
			if (retried === 'superseded') notify();
			return retried;
		} catch {
			try {
				const latest = await getById(id);
				if (!latest) {
					notify();
					return 'unknown';
				}
				if (!isInitialForegroundRequest(latest, options.idempotencyKey)) {
					notify();
					return 'superseded';
				}
				notify();
				return 'unknown';
			} catch {
				notify();
				return 'unknown';
			}
		}
	}
}

export interface AsyncDocumentSource {
	idempotencyKey: string;
	docKey: string;
	retryCount: number;
}

function hasAsyncDocumentIdentity(record: LocalQueueRecord, source: AsyncDocumentSource): boolean {
	return !!record.idempotencyKey &&
		(record.status === 'syncing' || record.status === 'pending') &&
		record.idempotencyKey === source.idempotencyKey &&
		record.docKey === source.docKey &&
		record.retryCount === source.retryCount &&
		record.republishLease == null;
}

function isAsyncDocumentOwner(record: LocalQueueRecord, source: AsyncDocumentSource): boolean {
	return hasAsyncDocumentIdentity(record, source) && isOutstandingAsyncStatus(record.asyncStatus);
}

export type MarkAsyncTerminalResult = 'updated' | 'already_applied' | 'superseded' | 'not_found';
export type DurableMarkAsyncTerminalResult = MarkAsyncTerminalResult | 'unknown';

export type MarkAsyncTerminalDecision =
	| { result: 'updated'; record: LocalQueueRecord }
	| { result: 'already_applied' | 'superseded' | 'not_found' };

export function decideMarkAsyncTerminal(
	existing: LocalQueueRecord | undefined,
	source: AsyncDocumentSource,
	target: Extract<PunchTaskStatus, 'succeeded' | 'failed'>,
): MarkAsyncTerminalDecision {
	if (!existing) return { result: 'not_found' };
	if (hasAsyncDocumentIdentity(existing, source) && existing.asyncStatus === target) {
		return existing.status === 'syncing' ?
			{ result: 'already_applied' } :
			{ result: 'updated', record: { ...existing, status: 'syncing' } };
	}
	if (!isAsyncDocumentOwner(existing, source)) return { result: 'superseded' };
	const terminal: LocalQueueRecord = { ...existing, status: 'syncing', asyncStatus: target };
	return {
		result: 'updated',
		// success 需要補 optimistic time；failed 只改 terminal marker，保留原時間供 retry context。
		record: target === 'succeeded' ?
			withPunchTimePair(terminal, existing.punchedAt ?? existing.createdAt) : terminal,
	};
}

export function decideMarkAsyncSucceeded(
	existing: LocalQueueRecord | undefined,
	source: AsyncDocumentSource,
): MarkAsyncTerminalDecision {
	return decideMarkAsyncTerminal(existing, source, 'succeeded');
}

export function decideMarkAsyncFailed(
	existing: LocalQueueRecord | undefined,
	source: AsyncDocumentSource,
): MarkAsyncTerminalDecision {
	return decideMarkAsyncTerminal(existing, source, 'failed');
}

async function markAsyncTerminal(
	id: string,
	source: AsyncDocumentSource,
	target: Extract<PunchTaskStatus, 'succeeded' | 'failed'>,
): Promise<MarkAsyncTerminalResult> {
	const db = await getDb();
	const tx = db.transaction(STORE_NAME, 'readwrite');
	observeTransactionRejection(tx);
	const store = tx.objectStore(STORE_NAME);
	const existing = await store.get(id);
	const decision = decideMarkAsyncTerminal(existing, source, target);
	if (decision.result !== 'updated') {
		await tx.done;
		if (decision.result === 'already_applied') notify();
		return decision.result;
	}
	await store.put(decision.record);
	await tx.done;
	notify();
	return 'updated';
}

export function markAsyncSucceeded(
	id: string,
	source: AsyncDocumentSource,
): Promise<MarkAsyncTerminalResult> {
	return markAsyncTerminal(id, source, 'succeeded');
}

export function markAsyncFailed(
	id: string,
	source: AsyncDocumentSource,
): Promise<MarkAsyncTerminalResult> {
	return markAsyncTerminal(id, source, 'failed');
}

async function markAsyncTerminalDurably(
	id: string,
	source: AsyncDocumentSource,
	target: Extract<PunchTaskStatus, 'succeeded' | 'failed'>,
): Promise<DurableMarkAsyncTerminalResult> {
	const operation = target === 'succeeded' ? markAsyncSucceeded : markAsyncFailed;
	try {
		const result = await operation(id, source);
		if (result === 'superseded') notify();
		return result;
	} catch {
		try {
			const retried = await operation(id, source);
			if (retried === 'already_applied' || retried === 'superseded') notify();
			return retried;
		} catch {
			try {
				const latest = await getById(id);
				if (!latest) return 'not_found';
				const decision = decideMarkAsyncTerminal(latest, source, target);
				if (decision.result === 'already_applied') {
					notify();
					return 'already_applied';
				}
				if (decision.result === 'superseded') {
					notify();
					return 'superseded';
				}
				notify();
				return 'unknown';
			} catch {
				notify();
				return 'unknown';
			}
		}
	}
}

export function markAsyncSucceededDurably(
	id: string,
	source: AsyncDocumentSource,
): Promise<DurableMarkAsyncTerminalResult> {
	return markAsyncTerminalDurably(id, source, 'succeeded');
}

export function markAsyncFailedDurably(
	id: string,
	source: AsyncDocumentSource,
): Promise<DurableMarkAsyncTerminalResult> {
	return markAsyncTerminalDurably(id, source, 'failed');
}

export type UpdateAsyncDocumentStatusDecision =
	| { result: 'updated'; record: LocalQueueRecord }
	| { result: 'superseded' | 'not_found' };

export function decideUpdateAsyncDocumentStatus(
	existing: LocalQueueRecord | undefined,
	source: AsyncDocumentSource,
	status: PunchTaskStatus,
): UpdateAsyncDocumentStatusDecision {
	if (!existing) return { result: 'not_found' };
	if (!isAsyncDocumentOwner(existing, source) || !isOutstandingAsyncStatus(status)) {
		return { result: 'superseded' };
	}
	return { result: 'updated', record: { ...existing, status: 'syncing', asyncStatus: status } };
}

export async function updateAsyncDocumentStatus(
	id: string,
	source: AsyncDocumentSource,
	status: PunchTaskStatus,
): Promise<'updated' | 'superseded' | 'not_found'> {
	const db = await getDb();
	const tx = db.transaction(STORE_NAME, 'readwrite');
	observeTransactionRejection(tx);
	const store = tx.objectStore(STORE_NAME);
	const decision = decideUpdateAsyncDocumentStatus(await store.get(id), source, status);
	if (decision.result !== 'updated') {
		await tx.done;
		return decision.result;
	}
	await store.put(decision.record);
	await tx.done;
	notify();
	return 'updated';
}

export function decideRemoveAsyncFailed(
	existing: LocalQueueRecord | undefined,
	source: AsyncDocumentSource,
): 'removed' | 'superseded' | 'not_found' {
	if (!existing) return 'not_found';
	return hasAsyncDocumentIdentity(existing, source) && existing.asyncStatus === 'failed' ?
		'removed' : 'superseded';
}

export async function removeAsyncFailed(
	id: string,
	source: AsyncDocumentSource,
): Promise<'removed' | 'superseded' | 'not_found'> {
	const db = await getDb();
	const tx = db.transaction(STORE_NAME, 'readwrite');
	observeTransactionRejection(tx);
	const store = tx.objectStore(STORE_NAME);
	const decision = decideRemoveAsyncFailed(await store.get(id), source);
	if (decision !== 'removed') {
		await tx.done;
		return decision;
	}
	await store.delete(id);
	await tx.done;
	notify();
	return 'removed';
}

// ============================================================
// publish_failed republish 的 guarded atomic transitions
// ============================================================

export interface RepublishSource {
	idempotencyKey: string;
	docKey: string;
	retryCount: number;
}

function isSameRepublishOwner(record: LocalQueueRecord, source: RepublishSource): boolean {
	return (record.status === 'syncing' || record.status === 'pending') &&
		record.idempotencyKey === source.idempotencyKey &&
		record.docKey === source.docKey &&
		record.retryCount === source.retryCount &&
		isOutstandingAsyncStatus(record.asyncStatus);
}

export const REPUBLISH_LEASE_MS = 60 * 1000;

export interface ClaimRepublishOptions {
	source: RepublishSource;
	ownerId: string;
	// 只有第一次 claim 會採用；active / expired lease 一律沿用已持久化的 next key。
	candidateNextIdempotencyKey: string;
	now?: number;
	leaseMs?: number;
}

export type ClaimRepublishResult =
	| { result: 'claimed'; lease: RepublishLease; record: LocalQueueRecord }
	| { result: 'busy'; leaseExpiresAt: number }
	| { result: 'superseded' | 'not_found' };

function leaseMatchesSource(lease: RepublishLease, source: RepublishSource): boolean {
	return lease.sourceIdempotencyKey === source.idempotencyKey &&
		lease.sourceDocKey === source.docKey &&
		lease.sourceRetryCount === source.retryCount;
}

export function decideRepublishClaim(
	existing: LocalQueueRecord | undefined,
	options: ClaimRepublishOptions,
	now: number,
): ClaimRepublishResult {
	if (!existing) return { result: 'not_found' };
	if (!isSameRepublishOwner(existing, options.source)) return { result: 'superseded' };

	const currentLease = existing.republishLease;
	if (currentLease && leaseMatchesSource(currentLease, options.source)) {
		// 同一次 transaction 在 commit 邊界回 reject 時，caller 以相同 owner 重試可確認 claim，
		// 不延長 lease，也不產生另一把 next key。
		if (currentLease.ownerId === options.ownerId && currentLease.expiresAt > now) {
			return { result: 'claimed', lease: currentLease, record: { ...existing, status: 'syncing' } };
		}
		if (currentLease.expiresAt > now) {
			return { result: 'busy', leaseExpiresAt: currentLease.expiresAt };
		}
	}

	const nextIdempotencyKey = currentLease && leaseMatchesSource(currentLease, options.source) ?
		currentLease.nextIdempotencyKey : options.candidateNextIdempotencyKey;
	const lease: RepublishLease = {
		sourceIdempotencyKey: options.source.idempotencyKey,
		sourceDocKey: options.source.docKey,
		sourceRetryCount: options.source.retryCount,
		ownerId: options.ownerId,
		nextIdempotencyKey,
		expiresAt: now + Math.max(1, options.leaseMs ?? REPUBLISH_LEASE_MS),
	};
	const record: LocalQueueRecord = { ...existing, status: 'syncing', republishLease: lease };
	return { result: 'claimed', lease, record };
}

// IndexedDB readwrite transactions 跨 tab serialize：只有第一個 claimant 能寫入 active lease；
// 其他 tab 讀到 busy，不會送 HTTP。lease 過期接手仍沿用 persisted nextIdempotencyKey。
export async function claimRepublish(
	id: string,
	options: ClaimRepublishOptions,
): Promise<ClaimRepublishResult> {
	const db = await getDb();
	const tx = db.transaction(STORE_NAME, 'readwrite');
	observeTransactionRejection(tx);
	const store = tx.objectStore(STORE_NAME);
	const existing = await store.get(id);
	const decision = decideRepublishClaim(existing, options, options.now ?? Date.now());
	if (decision.result !== 'claimed') {
		await tx.done;
		return decision;
	}
	await store.put(decision.record);
	await tx.done;
	notify();
	return decision;
}

function isAuthorizedRepublishClaim(
	record: LocalQueueRecord,
	source: RepublishSource,
	nextIdempotencyKey: string,
	leaseOwnerId?: string,
): boolean {
	if (!isSameRepublishOwner(record, source)) return false;
	// leaseOwnerId 未提供是部署前 caller / pure legacy case；舊紀錄沒有 lease 時保持相容。
	if (!record.republishLease) return leaseOwnerId == null;
	return leaseOwnerId != null &&
		record.republishLease.ownerId === leaseOwnerId &&
		record.republishLease.nextIdempotencyKey === nextIdempotencyKey &&
		leaseMatchesSource(record.republishLease, source);
}

export interface MarkRepublishedAcceptedOptions {
	source: RepublishSource;
	nextIdempotencyKey: string;
	nextDocKey: string;
	leaseOwnerId?: string;
	// 只有 foreground republish 會傳 server document_created_at；queue-origin 省略，維持原補登時間。
	punchedAt?: number;
}

export type MarkRepublishedAcceptedDecision =
	| { result: 'updated'; record: LocalQueueRecord }
	| { result: 'superseded' | 'not_found' };

export function isRepublishedAcceptedTransitionApplied(
	record: LocalQueueRecord | undefined,
	options: MarkRepublishedAcceptedOptions,
): boolean {
	return !!record &&
		record.idempotencyKey === options.nextIdempotencyKey &&
		record.docKey === options.nextDocKey &&
		record.retryCount === options.source.retryCount &&
		record.asyncStatus != null &&
		record.republishLease == null;
}

export function decideMarkRepublishedAccepted(
	existing: LocalQueueRecord | undefined,
	options: MarkRepublishedAcceptedOptions,
): MarkRepublishedAcceptedDecision {
	if (!existing) return { result: 'not_found' };
	// completion promise ambiguous 時，以相同 next key/doc 重試只確認已套用狀態，不再觸發 HTTP。
	if (isRepublishedAcceptedTransitionApplied(existing, options)) {
		return { result: 'updated', record: existing };
	}
	if (!isAuthorizedRepublishClaim(
		existing,
		options.source,
		options.nextIdempotencyKey,
		options.leaseOwnerId,
	)) return { result: 'superseded' };
	const accepted: LocalQueueRecord = {
		...existing,
		idempotencyKey: options.nextIdempotencyKey,
		docKey: options.nextDocKey,
		serverAcceptedAt: existing.serverAcceptedAt ?? Date.now(),
		asyncStatus: 'accepted',
		republishLease: undefined,
	};
	return {
		result: 'updated',
		record: options.punchedAt != null ? withPunchTimePair(accepted, options.punchedAt) : accepted,
	};
}

export async function markRepublishedAccepted(
	id: string,
	options: MarkRepublishedAcceptedOptions,
): Promise<'updated' | 'superseded' | 'not_found'> {
	const db = await getDb();
	const tx = db.transaction(STORE_NAME, 'readwrite');
	observeTransactionRejection(tx);
	const store = tx.objectStore(STORE_NAME);
	const existing = await store.get(id);
	const decision = decideMarkRepublishedAccepted(existing, options);
	if (decision.result !== 'updated') {
		await tx.done;
		return decision.result;
	}
	await store.put(decision.record);
	await tx.done;
	notify();
	return 'updated';
}

export interface RepublishRetryFailureOptions {
	source: RepublishSource;
	nextIdempotencyKey: string;
	leaseOwnerId?: string;
	deferredReason: string;
	lastError?: string;
	// foreground 傳 createdAt；queue-origin 傳原 punchedAt ?? createdAt。
	punchedAt: number;
	// production caller 省略；pure test 可固定時間。
	now?: number;
}

export type RepublishRetryFailureResult = 'scheduled' | 'exhausted' | 'superseded' | 'not_found';

export type RepublishRetryFailureDecision =
	| { result: 'scheduled' | 'exhausted'; record: LocalQueueRecord }
	| { result: 'superseded' | 'not_found' };

export function getAppliedRepublishRetryResult(
	record: LocalQueueRecord | undefined,
	options: RepublishRetryFailureOptions,
): Extract<RepublishRetryFailureResult, 'scheduled' | 'exhausted'> | undefined {
	if (!record ||
		record.idempotencyKey !== options.nextIdempotencyKey ||
		record.docKey != null ||
		record.asyncStatus != null ||
		record.republishLease != null ||
		record.allowResultModal !== false ||
		record.retryCount !== options.source.retryCount + 1
	) return undefined;
	if (record.status === 'pending') return 'scheduled';
	if (record.status === 'inactive') return 'exhausted';
	return undefined;
}

export function decideRepublishRetryFailure(
	existing: LocalQueueRecord | undefined,
	options: RepublishRetryFailureOptions,
	now: number,
): RepublishRetryFailureDecision {
	if (!existing) return { result: 'not_found' };
	// transaction completion promise 若在 commit 邊界失敗，caller 會用同一把 next key 重試確認。
	// 唯一 key + retryCount 可辨識「上一輪其實已完整套用」，回傳原結果讓 caller 能 kick / 發 outcome，
	// 不把已成功的 ambiguous commit 誤報成 superseded。
	const appliedResult = getAppliedRepublishRetryResult(existing, options);
	if (appliedResult) return { result: appliedResult, record: existing };
	if (!isAuthorizedRepublishClaim(
		existing,
		options.source,
		options.nextIdempotencyKey,
		options.leaseOwnerId,
	)) return { result: 'superseded' };

	const newRetryCount = existing.retryCount + 1;
	const base: LocalQueueRecord = {
		...existing,
		idempotencyKey: options.nextIdempotencyKey,
		docKey: undefined,
		serverAcceptedAt: existing.serverAcceptedAt ?? now,
		asyncStatus: undefined,
		allowResultModal: false,
		deferredReason: existing.deferredReason ?? options.deferredReason,
		lastError: options.lastError ?? options.deferredReason,
		retryCount: newRetryCount,
		republishLease: undefined,
	};
	const withTime = withPunchTimePair(base, options.punchedAt);
	if (newRetryCount >= MAX_RETRY_COUNT) {
		return { result: 'exhausted', record: { ...withTime, status: 'inactive' } };
	}
	return {
		result: 'scheduled',
		record: {
			...withTime,
			status: 'pending',
			nextRetryAt: now + RETRY_BACKOFF_MS[newRetryCount],
		},
	};
}

// publish_failed republish 再遇 retryable error：ownership 換 key、清 Firestore handle、
// 寫 retry metadata 與 backoff 全部在同一 transaction / put 完成，scheduler 看不到中間 pending。
export async function transitionRepublishRetryFailure(
	id: string,
	options: RepublishRetryFailureOptions,
): Promise<RepublishRetryFailureResult> {
	const db = await getDb();
	const tx = db.transaction(STORE_NAME, 'readwrite');
	observeTransactionRejection(tx);
	const store = tx.objectStore(STORE_NAME);
	const existing = await store.get(id);
	const decision = decideRepublishRetryFailure(existing, options, options.now ?? Date.now());
	if (!('record' in decision)) {
		await tx.done;
		return decision.result;
	}
	await store.put(decision.record);
	await tx.done;
	notify();
	return decision.result;
}

export interface FinalizeRepublishFatalOptions {
	source: RepublishSource;
	nextIdempotencyKey: string;
	leaseOwnerId?: string;
}

export function decideFinalizeRepublishFatal(
	existing: LocalQueueRecord | undefined,
	options: FinalizeRepublishFatalOptions,
): 'removed' | 'superseded' | 'not_found' {
	if (!existing) return 'not_found';
	return isAuthorizedRepublishClaim(
		existing,
		options.source,
		options.nextIdempotencyKey,
		options.leaseOwnerId,
	) ? 'removed' : 'superseded';
}

// republish HTTP 4xx 的 terminal delete 也必須驗證 lease。舊 owner 在 callback 延遲期間
// 若已被另一個 tab 接手，不能刪掉新 owner 的 durable claim / 後續結果。
export async function finalizeRepublishFatal(
	id: string,
	options: FinalizeRepublishFatalOptions,
): Promise<'removed' | 'superseded' | 'not_found'> {
	const db = await getDb();
	const tx = db.transaction(STORE_NAME, 'readwrite');
	observeTransactionRejection(tx);
	const store = tx.objectStore(STORE_NAME);
	const existing = await store.get(id);
	const decision = decideFinalizeRepublishFatal(existing, options);
	if (decision !== 'removed') {
		await tx.done;
		return decision;
	}
	await store.delete(id);
	await tx.done;
	notify();
	return 'removed';
}

// ============================================================
// Visibility timeout（stale syncing 回收）
// ============================================================

// 前景 /enqueue 在途中 tab 被關 / 凍結時，onSuccess / onError 永遠不會執行，紀錄會卡在
// syncing（nextRetryAt = 0，scheduler 不撿）——UI 顯示樂觀打卡時間，但 server 其實沒收到。
// scheduler 每次 scan（mount / visibilitychange / kick / 回收 timer 到期）呼叫 reclaimStaleSyncing
// 把這種紀錄拉回 pending 重送。回收 timer 由 scheduleNext 依 getNextReclaimAt 排定，讓前景一直
// 停留頁面（沒有 visibilitychange）時也能在門檻到期後被自動撿走。
//
// 門檻必須大於 PUNCH_API_TIMEOUT_MS（30s，見 utils/punch/punchQueueClient）：超過門檻還停在
// syncing 的紀錄，其原始請求必然已 timeout 或 callback 永遠不會來，不存在「請求還在飛就被回收」的 race。
export const STALE_SYNCING_RECLAIM_MS = 40 * 1000;

// 回收 timer 的排程誤差容忍：timer 目標在起算點 + STALE_SYNCING_RECLAIM_MS 醒來，setTimeout
// 可能早醒少許；門檻判定放寬這個量，避免差幾 ms 不合格 → 該輪空跑、又要重排一輪 timer。
// 放寬後的有效門檻（39s）仍大於 PUNCH_API_TIMEOUT_MS，上面的不變式不受影響。
export const RECLAIM_JITTER_TOLERANCE_MS = 1000;

// 門檻的起算點：V2 request 實際送出的時刻。**不能用 createdAt**——它壓在 await enqueue() 之前，
// 而分頁 / App 被凍結時（手機鎖屏、切到背景）那個 await 的 continuation 根本不會執行，
// 恢復後才 dispatch。凍結 20s 就讓「39s 門檻」早於「該請求自己的 30s timeout（落在 50s）」，
// 回收會撿走一筆還在飛的正常請求。舊紀錄沒有 durable baseline 時不回收，留到 TTL。
export function reclaimBaselineAt(record: LocalQueueRecord): number {
	return record.queueRecordVersion === CURRENT_QUEUE_RECORD_VERSION &&
		typeof record.requestStartedAt === 'number' && Number.isFinite(record.requestStartedAt) ?
		record.requestStartedAt : Number.POSITIVE_INFINITY;
}

function getAsyncRetryClaimReclaimAt(record: LocalQueueRecord): number | undefined {
	const expiresAt = record.asyncRetryClaim?.expiresAt;
	if (
		record.status !== 'syncing' ||
		!record.idempotencyKey ||
		record.docKey != null ||
		record.asyncStatus != null ||
		record.republishLease != null ||
		!record.asyncRetryClaim?.claimId ||
		typeof expiresAt !== 'number' ||
		!Number.isFinite(expiresAt)
	) return undefined;
	return expiresAt;
}

// 單筆 syncing record 的絕對回收時刻：initial foreground 從 durable requestStartedAt 起算；
// 新 bundle 的 async retry 使用 persisted claim expiry。舊 syncing / sync / 無 key machine 無回收時刻。
export function getRecordReclaimAt(record: LocalQueueRecord): number | undefined {
	const retryClaimAt = getAsyncRetryClaimReclaimAt(record);
	if (retryClaimAt != null) return retryClaimAt;
	if (
		isInitialForegroundRequest(record) &&
		typeof record.requestStartedAt === 'number' &&
		Number.isFinite(record.requestStartedAt)
	) return record.requestStartedAt + STALE_SYNCING_RECLAIM_MS;
	return undefined;
}

// 回收候選的靜態條件（不含 now，純函式，供測試）：
//   - initial foreground V2 request：依 durable requestStartedAt 回收；
//   - 新 bundle 已 claim 的 async retry：依 persisted claimId / expiresAt 回收；
//   - 舊 bundle 留下的 syncing（沒有 claim）、同步紀錄與無 key 的舊 machine 沒有 fencing，維持到 TTL；
//   - 已交棒 BE 的 docKey / asyncStatus 由 firestorePunchWatcher 收尾。
export function isReclaimCandidate(record: LocalQueueRecord): boolean {
	return getRecordReclaimAt(record) != null;
}

// 回收條件（純函式，供測試）＝ 靜態候選條件 + 時間門檻（含 timer 誤差容忍）
export function isStaleSyncingRecord(record: LocalQueueRecord, now: number): boolean {
	const reclaimAt = getRecordReclaimAt(record);
	return reclaimAt != null && now > reclaimAt - RECLAIM_JITTER_TOLERANCE_MS;
}

// 最早一筆回收候選的「到期時刻」（絕對時間戳）；沒有候選回 undefined。
// 角色對齊 getNextPending：scheduler 的 scheduleNext 用它排回收 timer，到期跑 scan → 開頭的
// reclaimStaleSyncing 撿走候選、同輪 listReadyForRetry 直接重送。202 先到（markAccepted 押上
// docKey/asyncStatus）候選就消失，timer 到期只是空跑一輪 scan，無害。
export async function getNextReclaimAt(accountId: string): Promise<number | undefined> {
	const syncing = await getByStatus(accountId, 'syncing');
	const reclaimTimes = syncing
		.map(getRecordReclaimAt)
		.filter((value): value is number => value != null);
	if (reclaimTimes.length === 0) return undefined;
	return Math.min(...reclaimTimes);
}

export const STALE_SYNCING_RECLAIM_REASON = 'stale_syncing_reclaim';

export interface ReclaimStaleSyncingSummary {
	moved: number;
	skipped: number;
	writeFailed: number;
}

export type ReclaimAttemptResult = EnterRetryQueueResult | 'write_failed';

export function summarizeReclaimAttempts(results: ReclaimAttemptResult[]): ReclaimStaleSyncingSummary {
	return results.reduce<ReclaimStaleSyncingSummary>((summary, result) => {
		if (result === 'moved') return { ...summary, moved: summary.moved + 1 };
		if (result === 'write_failed') return { ...summary, writeFailed: summary.writeFailed + 1 };
		return { ...summary, skipped: summary.skipped + 1 };
	}, { moved: 0, skipped: 0, writeFailed: 0 });
}

// 把 stale syncing 紀錄拉回 pending（nextRetryAt = now → 本次 scan 就會重送）。
// guard 不成立才算 skipped；transaction reject 明確算 writeFailed，讓 scheduler 啟動 storage backoff。
export async function reclaimStaleSyncing(
	accountId: string,
	now: number,
): Promise<ReclaimStaleSyncingSummary> {
	const candidates = (await getByStatus(accountId, 'syncing')).filter(record => isStaleSyncingRecord(record, now));
	if (candidates.length === 0) return { moved: 0, skipped: 0, writeFailed: 0 };

	const results: ReclaimAttemptResult[] = await Promise.all(candidates.map(async candidate => {
		try {
			return await enterRetryQueue(candidate.id, {
				deferredReason: STALE_SYNCING_RECLAIM_REASON,
				// 回收後要立刻重送，不等 backoff
				nextRetryAt: now,
				// 讀出候選到寫入之間 202 或新 claim 可能已接手；除了仍過期，也要比對
				// 原 claimId（foreground 則比對 request baseline），不可回收 replacement owner。
				guard: record => isStaleSyncingRecord(record, now) && (
					candidate.asyncRetryClaim ?
						record.asyncRetryClaim?.claimId === candidate.asyncRetryClaim.claimId :
						record.asyncRetryClaim == null &&
						record.idempotencyKey === candidate.idempotencyKey &&
						record.requestStartedAt === candidate.requestStartedAt
				),
			});
		} catch (e) {
			return 'write_failed';
		}
	}));
	return summarizeReclaimAttempts(results);
}

// ============================================================
// 維護
// ============================================================

// 清掉所有 createdAt 早於 now - ttlMs 的紀錄，不管 status 是什麼，回傳刪掉的筆數
export async function purgeExpired(now: number, ttlMs: number = TTL_MS): Promise<number> {
	const db = await getDb();
	const cutoff = now - ttlMs;
	const range = IDBKeyRange.upperBound(cutoff, true);
	const keys = await db.getAllKeysFromIndex(STORE_NAME, INDEX_BY_CREATED, range);
	await Promise.all(keys.map(key => db.delete(STORE_NAME, key)));
	if (keys.length > 0) notify();
	return keys.length;
}
